import axios from 'axios';
import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from 'components/AORedesign';
import { handleOnChange } from 'components/CommonUtilityServices/EventHandlerService';
import { USER_ADMIN_DUPLICATE_EMAIL_DELETE, USER_ADMIN_DUPLICATE_EMAIL_SEARCH } from 'constants/api';

const defaultFormData = { email: '' };

const UserAdminDeleteDuplicateEmail = () => {
  const [formData, setFormData] = useState(defaultFormData);
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isRequestFailed, setIsRequestFailed] = useState(false);

  const handleClearOnClick = () => {
    setFormData(defaultFormData);
    setSearchResult(null);
    setNotFound(false);
    setStatusMessage(null);
  };

  const handleSearchOnClick = async () => {
    setSearchLoading(true);
    setSearchResult(null);
    setNotFound(false);
    setStatusMessage(null);
    try {
      const searchUrl = `${USER_ADMIN_DUPLICATE_EMAIL_SEARCH}?email=${encodeURIComponent(formData.email)}`;
      const response = await axios.get(searchUrl);
      if (response.status === 204 || !response.data) {
        setNotFound(true);
      } else {
        setSearchResult(response.data);
      }
    } catch (error) {
      console.error(error);
      setNotFound(true);
    }
    setSearchLoading(false);
  };

  const handleOkClick = async () => {
    setDeleteLoading(true);
    setStatusMessage(null);
    try {
      const deleteUrl = `${USER_ADMIN_DUPLICATE_EMAIL_DELETE}/${searchResult.userName}?email=${encodeURIComponent(searchResult.email)}`;
      const response = await axios.delete(deleteUrl);
      if (response.status === 200) {
        setStatusMessage('Email successfully deleted.');
        setIsRequestFailed(false);
        setSearchResult(null);
      } else {
        setStatusMessage('Failed to delete the email. Please try again.');
        setIsRequestFailed(true);
      }
    } catch (error) {
      console.error(error);
      setStatusMessage('Failed to delete the email. Please try again.');
      setIsRequestFailed(true);
    }
    setDeleteLoading(false);
    setPendingDelete(false);
  };

  return (
    <Card>
      <Box mb={2}>
        <Typography variant="h6">Delete Duplicate Email</Typography>
      </Box>

      <Box mb={2}>
        <TextField
          id="delete-duplicate-email-input"
          label="Email"
          name="email"
          onChange={(e) => handleOnChange(e, formData, setFormData)}
          placeholder="Enter email address"
          value={formData.email}
        />
      </Box>

      <Box display="flex" alignItems="center">
        <Button id="delete-duplicate-email-button-search" disabled={!formData.email || searchLoading} onClick={handleSearchOnClick}>
          Search
        </Button>
        <Box mr={1} />
        <Button variant="outlined" id="delete-duplicate-email-button-clear" onClick={handleClearOnClick}>
          Clear
        </Button>
        {searchLoading && (
          <Box ml={2}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>

      {statusMessage && <Alert severity={isRequestFailed ? 'error' : 'success'} message={statusMessage} />}

      {notFound && (
        <Box mt={2}>
          <Typography color="error">Email not Found.</Typography>
        </Box>
      )}

      {searchResult && (
        <Box mt={3}>
          <Paper variant="outlined" square>
            <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography>
                  <strong>User Name:</strong> {searchResult.userName}
                </Typography>
                <Typography>
                  <strong>Email:</strong> {searchResult.email}
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => setPendingDelete(true)}>
                Delete
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <Dialog open={pendingDelete} onClose={() => setPendingDelete(false)}>
        <DialogTitle>Delete this email?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the email {searchResult?.email} for user {searchResult?.userName}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPendingDelete(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleOkClick} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Ok'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default UserAdminDeleteDuplicateEmail;
